import { Controller } from '@hotwired/stimulus'

export type CardController = {
  createToken: (billingDetails?: any) => Promise<{ error?: unknown, token?: { id: string } }>
  handleNextAction: (clientSecret: string) => Promise<any>
}

export type AddressController = {
  validate: () => boolean
  getBillingDetails: () => { name: string, address: any }
}

/**
 * Base controller for Stripe payment and card setup forms
 * Mirrors the Ruby TemplateComponent pattern - provides common payment form logic
 * Handles: outlet management, token creation, form submission
 * Used by: TransactionComponentController (payments), SetupComponentController (card updates)
 */
export default class extends Controller {
  static targets = ['form']
  static outlets = ['payments--stripe--card-component', 'payments--stripe--address-component']
  static values = {
    requireCard: { type: Boolean, default: true }
  }

  declare formTarget: HTMLFormElement
  declare requireCardValue: boolean
  declare hasPaymentsStripeCardComponentOutlet: boolean
  declare paymentsStripeCardComponentOutlet: CardController
  declare hasPaymentsStripeAddressComponentOutlet: boolean
  declare paymentsStripeAddressComponentOutlet: AddressController
  protected isReady = false
  protected logPrefix = 'StripeForm' // Override in subclasses

  connect() {
    this.refreshReadiness()
    console.log(`[${this.logPrefix}] Controller connected`)
  }

  disconnect() {
    this.isReady = false
  }

  paymentsStripeCardComponentOutletConnected() {
    console.log(`[${this.logPrefix}] Card controller connected`)
    this.refreshReadiness()
  }

  paymentsStripeCardComponentOutletDisconnected() {
    this.refreshReadiness()
  }

  paymentsStripeAddressComponentOutletConnected() {
    console.log(`[${this.logPrefix}] Address controller connected`)
    this.refreshReadiness()
  }

  paymentsStripeAddressComponentOutletDisconnected() {
    this.refreshReadiness()
  }

  async submit(event: Event) {
    console.log(`[${this.logPrefix}] Submit triggered`)
    event.preventDefault()
    this.disableSubmitButton()

    if (!this.isReady) {
      console.warn(`[${this.logPrefix}] Required dependencies are not ready`)
      console.log(`[${this.logPrefix}] - hasCard:`, this.hasPaymentsStripeCardComponentOutlet)
      console.log(`[${this.logPrefix}] - hasAddress:`, this.hasPaymentsStripeAddressComponentOutlet)
      this.enableSubmitButton()
      return
    }

    try {
      if (this.hasPaymentsStripeAddressComponentOutlet && !this.paymentsStripeAddressComponentOutlet.validate()) {
        console.warn(`[${this.logPrefix}] Address validation failed`)
        return
      }

      if (!this.requireCardValue) {
        console.log(`[${this.logPrefix}] Submitting without card tokenization`)
        await this.submitFormViaFetch()
        return
      }

      // Get billing details from address component before creating token
      const billingDetails = this.hasPaymentsStripeAddressComponentOutlet
        ? this.paymentsStripeAddressComponentOutlet.getBillingDetails()
        : undefined

      const result = await this.paymentsStripeCardComponentOutlet.createToken(billingDetails)
      if (result.error) {
        console.error(`[${this.logPrefix}] Token creation failed`)
        return
      }

      if (result.token) {
        console.log(`[${this.logPrefix}] Token created successfully`)
        await this.submitToken(result.token.id)
      }
    } catch (error) {
      console.error(`[${this.logPrefix}] Token creation failed`)
    } finally {
      this.enableSubmitButton()
    }
  }

  protected refreshReadiness(): void {
    const hasCard = this.hasPaymentsStripeCardComponentOutlet
    const hasAddress = this.hasPaymentsStripeAddressComponentOutlet

    this.isReady = (!this.requireCardValue || hasCard) && hasAddress

    if (this.requireCardValue && !hasCard) {
      console.warn(`[${this.logPrefix}] Card controller not found`)
    }

    if (!hasAddress) {
      console.warn(`[${this.logPrefix}] Address controller not found`)
    }

    if (this.isReady) {
      console.log(`[${this.logPrefix}] Card and Address controllers are live and ready`)
    }
  }

  protected disableSubmitButton(): void {
    const submitButton = this.element.querySelector<HTMLButtonElement>('.credit-card-form-component__submit-button')
    if (submitButton) {
      submitButton.disabled = true
    }
  }

  protected enableSubmitButton(): void {
    const submitButton = this.element.querySelector<HTMLButtonElement>('.credit-card-form-component__submit-button')
    if (submitButton) {
      submitButton.disabled = false
    }
  }

  protected async submitToken(tokenId: string): Promise<void> {
    const hiddenInput = document.createElement('input')
    hiddenInput.setAttribute('type', 'hidden')
    hiddenInput.setAttribute('name', 'stripe_token')
    hiddenInput.setAttribute('value', tokenId)
    this.formTarget.appendChild(hiddenInput)
    await this.submitFormViaFetch()
  }

  private async submitFormViaFetch(): Promise<void> {
    const formData = new FormData(this.formTarget)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

    try {
      const response = await fetch(this.formTarget.action, {
        method: this.formTarget.method || 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      const data = await response.json()

      if (data.status === 'success') {
        window.location.href = data.redirect_url
      } else if (data.status === 'requires_action') {
        console.log(`[${this.logPrefix}] 3DS authentication required`)
        await this.handle3dsAuthentication(data.client_secret, data.payment_intent_id, data.confirm_url)
      } else if (data.status === 'error') {
        this.showPaymentError(data.message || 'Payment failed. Please try again.')
      }
    } catch (error) {
      console.error(`[${this.logPrefix}] Form submission failed`)
      this.showPaymentError(this.submissionErrorMessage)
    }
  }

  // Overridden by setup-mode forms, where no charge is attempted
  protected get submissionErrorMessage(): string {
    return 'An error occurred confirming this transaction. Please check your payment status and contact support if you were charged but did not receive confirmation.'
  }

  private async handle3dsAuthentication(clientSecret: string, paymentIntentId: string, confirmUrl?: string): Promise<void> {
    if (!this.hasPaymentsStripeCardComponentOutlet) {
      console.error(`[${this.logPrefix}] Card controller not available for 3DS authentication`)
      this.showPaymentError('Payment authentication is not available. Please refresh and try again.')
      return
    }

    try {
      const { error } = await this.paymentsStripeCardComponentOutlet.handleNextAction(clientSecret)

      if (error) {
        console.error(`[${this.logPrefix}] 3DS authentication failed`)
      } else {
        console.log(`[${this.logPrefix}] 3DS authentication succeeded, confirming payment`)
      }

      // Always call confirmPayment — the backend is the source of truth on PI status
      // and will revert the election state if 3DS was not completed successfully.
      await this.confirmPayment(paymentIntentId, confirmUrl)
    } catch (error) {
      console.error(`[${this.logPrefix}] 3DS authentication error`)
      this.showPaymentError('Authentication failed. Please try again.')
    }
  }

  private async confirmPayment(paymentIntentId: string, confirmUrl?: string): Promise<void> {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const url = confirmUrl || this.buildConfirmUrl()

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ payment_intent_id: paymentIntentId })
      })

      const data = await response.json()

      if (data.status === 'success') {
        window.location.href = data.redirect_url
      } else {
        this.showPaymentError(data.message || 'Payment confirmation failed. Please try again.')
      }
    } catch (error) {
      console.error(`[${this.logPrefix}] Payment confirmation failed`)
      this.showPaymentError(this.submissionErrorMessage)
    }
  }

  private buildConfirmUrl(): string {
    const action = this.formTarget.action
    try {
      const url = new URL(action)
      const pathParts = url.pathname.split('/')
      const paymentsIndex = pathParts.indexOf('payments')
      if (paymentsIndex >= 0) {
        url.pathname = pathParts.slice(0, paymentsIndex + 1).join('/') + '/confirm'
      }
      return url.toString()
    } catch {
      const parts = action.split('/')
      const idx = parts.indexOf('payments')
      if (idx >= 0) {
        return parts.slice(0, idx + 1).join('/') + '/confirm'
      }
      return action + '/confirm'
    }
  }

  private showPaymentError(message: string): void {
    if (this.hasPaymentsStripeCardComponentOutlet) {
      const cardOutlet = this.paymentsStripeCardComponentOutlet as any
      if (cardOutlet.cardErrorsTarget) {
        cardOutlet.cardErrorsTarget.textContent = message
        cardOutlet.cardErrorsTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
    }
    const flashContainer = document.querySelector('.flash-messages, .alert-container, [data-flash-target]')
    if (flashContainer) {
      flashContainer.innerHTML = `<div class="alert alert-danger">${message}</div>`
      flashContainer.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      alert(message)
    }
  }
}
